import React, { FC, useCallback, useRef, useState } from "react";
import {
  detectFileIconType,
  detectProjectType,
} from "../../../AgencyFileDrop/utils";
import { Tooltip } from "antd";
import uploadIcon from "../../../../assets/svg/empty_folders.svg";
import removeIcon from "../../../../assets/svg/removeUploadedFileIcon.svg";

import RandomPreview from "../../../../assets/img/random_preview.png";

import { uniqueId } from "lodash";
import "./index.scss";
import { openToast } from "../../../../Toasts";
import DeleteResourceModal from "../../../AgencyFileDrop/components/DeleteResourceModal";
import cancelIcon from "../../../../assets/svg/circledCancelIcon.svg";
import { useDispatch } from "react-redux";
import { deleteUploadedFile } from "../../../../store/actions/raiseAdRequest";

interface AdUploadFilesProps {
  accept: string;
  isHalfWidthReq?: boolean;
  uploadTitle?: string;
  fileList: any;
  setFileList: any;
  currentStage: string;
  prefix?: string;
  isUesrPermitted?: boolean;
}
const MAX_SIZE = 10 * 1024 * 1024;
export const AdUploadFiles: FC<AdUploadFilesProps> = ({
  isHalfWidthReq = false,
  uploadTitle = "Upload from computer",
  accept,
  fileList,
  setFileList,
  currentStage,
  prefix,
  isUesrPermitted = true,
}) => {
  const dispatch = useDispatch();
  const fileUpload = useRef<any>(null);
  const [isDeletedModalOpen, setIdDeletedModalOpen] = useState<boolean>(false);
  const [deletingFileId, setDeletingFileId] = useState<number>(0);

  const handleChange = useCallback(
    (e: any) => {
      let files: any[] = Array.from(e?.target?.files || []); // Convert FileList to an array

      const updatedFiles = files
        .map((file) => {
          if (file.size > MAX_SIZE) {
            openToast("error", "File Size should be smaller than 10MB.");
            return null;
          }

          const newName = !!prefix ? `${prefix}_${file.name}` : file.name;

          // Create a new File with the updated name
          const newFile = new File([file], newName, { type: file.type });

          (newFile as any).uid = uniqueId("file_");
          (newFile as any).thumbUrl = URL.createObjectURL(newFile);
          (newFile as any).parent = 0;

          return newFile;
        })
        .filter(Boolean); // remove nulls (invalid files)

      setFileList((f: any) => [...f, ...updatedFiles]);
    },
    [prefix, setFileList]
  );

  const handleDeleteUploadedData = useCallback(
    (index, file) => {
      if (!isUesrPermitted) return;
      if (!!file?.download_url) {
        setIdDeletedModalOpen(true);
        setDeletingFileId(file.id);
      } else {
        setFileList((prevFileList) => {
          const updatedFileList = [...prevFileList];
          updatedFileList.splice(index, 1);
          return updatedFileList;
        });
      }
    },
    [isUesrPermitted, setFileList]
  );

  const handleDeleteUploadedFile = useCallback(() => {
    dispatch(deleteUploadedFile({ id: deletingFileId, stage: currentStage }));
    setIdDeletedModalOpen(false);
    setDeletingFileId(0);
  }, [currentStage, deletingFileId, dispatch]);
  return (
    <div
      className={`upload-modal ${isHalfWidthReq ? "upload-modal--half" : ""}`}
    >
      <div
        className="upload-wrapper"
        style={{ position: !fileList.length ? "relative" : "inherit" }}
      >
        <div style={{ marginTop: "3rem" }}>
          {!fileList.length ? (
            <img src={uploadIcon} alt="Upload Icon" />
          ) : (
            <></>
          )}

          {!!fileList.length && (
            <div className="uploaded-files-wrapper">
              {fileList.map((file, fileIndex) => {
                const fileName = file?.name ?? `${file?.title}.${file?.content_type}`;
                const extension: string =
                  fileName
                    ?.split(".")
                    ?.pop()
                    ?.toLowerCase() || "";

                const fileType = file?.type ?? file?.content_type;
                const url: string =
                  fileType?.includes("image/") && extension !== "psd"
                    ? file.thumbUrl
                    : detectProjectType({ ...file, file_type: extension });

                return (
                  <div key={file.uid || file.id} className="uploaded-file">
                    <div
                      style={{
                        position: "relative",
                        display: "inline-block",
                      }}
                    >
                      <div
                        className="deleteFileIcon"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevents triggering the file input
                          handleDeleteUploadedData(fileIndex, file);
                        }}
                      >
                        <img src={removeIcon} alt="Remove Icon" />
                      </div>
                      <div className="fileTypeIconWrapper">
                        <img
                          src={detectFileIconType({
                            ...file,
                            file_url: url,
                            file_type: extension,
                          })}
                          alt="File Icon"
                        />
                      </div>
                    </div>
                    <div className="file_wrapper">
                      {url && (
                        <img
                          className=" file_image_local"
                          src={url}
                          onError={(e: any) => {
                            e.target.onerror = null;
                            e.target.src = RandomPreview;
                          }}
                          alt="fileImage"
                        />
                      )}
                    </div>
                    <div
                      className="file_content_wrapper"
                      style={{ paddingTop: "2rem !important" }}
                    >
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          paddingTop: "2rem !important",
                        }}
                      >
                        <Tooltip placement="bottomRight" title={fileName}>
                          <div className="image_title">{fileName}</div>
                        </Tooltip>
                      </div>
                      <div></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ position: !!fileList.length ? "relative" : "inherit" }}>
            <div className="dragAndDropTitle">Drag and drop your files</div>
            <div className="uploadTitle" style={{ color: "#6B38C4" }}>
              {uploadTitle}
            </div>
            <input
              key={fileList.length ? "input-open" : "input-closed"}
              accept={accept}
              ref={fileUpload}
              multiple
              type="file"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: "pointer",
              }}
              onClick={(e: any) => {
                if (fileList?.length > 0) {
                  // ⛔ Prevent opening the file picker
                  e.preventDefault();
                  e.stopPropagation();
                  openToast("info", "Please remove the uploaded file first.");
                  return false;
                }
                e.target.value = null; // reset for re-upload
              }}
              onChange={handleChange}
              disabled={!isUesrPermitted}
            />
          </div>
        </div>
      </div>

      <DeleteResourceModal
        isModalOpen={isDeletedModalOpen}
        setModalOpen={setIdDeletedModalOpen}
        modalTitle="Delete File?"
        modalDesc="Are you sure you want to delete this file."
        handleDeleteClick={handleDeleteUploadedFile}
        img={cancelIcon}
      />
    </div>
  );
};
